<script setup>
/**
 * 应用外壳（结构与样式照 shadiao-2d 运营后台）：左侧按主流程排的菜单，顶栏只放当前公会和用户信息。
 * 以某个 Agent 身份看的页面（路由 meta.guild）要等当前公会定下来、并确认这个 Agent 在这个公会在册，才渲染页面。
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, Select, ToastHost } from '@drama/ui'

import { agentsIn, currentGuild, loadAccount, selectGuild, session, setSession } from './api.js'
import { LOCALES, saveLocale } from './i18n/locale.js'
import GuildDocLink from './components/GuildDocLink.vue'
import InboxBell from './components/InboxBell.vue'
import LoginModal from './LoginModal.vue'

const { t, locale } = useI18n()
const router = useRouter()
const route = useRoute()

// 登录后、以及每次换页时重新取账号：别人的操作（运营批准建公会、负责人批准加入）会改变我能看的公会。
watch([() => session.loggedIn, () => route.path], ([loggedIn]) => {
  if (loggedIn) loadAccount()
}, { immediate: true })

// —— 主题：手动选过就用选过的，否则跟系统。生效值落到 <html data-theme>，@drama/ui 的 token 整体换肤。
const THEME_KEY = 'voko.theme'
const theme = ref(localStorage.getItem(THEME_KEY) ?? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'))
watch(theme, (value) => document.documentElement.setAttribute('data-theme', value), { immediate: true })
function toggleTheme() {
  theme.value = theme.value === 'dark' ? 'light' : 'dark'
  localStorage.setItem(THEME_KEY, theme.value)
}

/** @param {string} value */
function changeLocale(value) {
  locale.value = value
  saveLocale(value)
}
watch(locale, (value) => document.documentElement.setAttribute('lang', value), { immediate: true })

// —— 侧栏：宽屏可以收起成只剩图标（记在本浏览器）；手机宽度下是抽屉，换页就关上。
const COLLAPSED_KEY = 'voko.nav_collapsed'
const collapsed = ref(localStorage.getItem(COLLAPSED_KEY) === '1')
const drawer = ref(false)
function toggleCollapsed() {
  collapsed.value = !collapsed.value
  localStorage.setItem(COLLAPSED_KEY, collapsed.value ? '1' : '0')
}
const userMenu = ref(null)
watch(() => route.fullPath, () => {
  drawer.value = false
  if (userMenu.value !== null) userMenu.value.open = false
})

/** 我在当前公会在册的 Agent；看板、公会城用排第一个的（公司 Agent 优先）。 */
const here = computed(() => (currentGuild.slug === null ? [] : agentsIn(currentGuild.slug)))
const isAdmin = computed(() => session.user !== null && session.user.role === 'admin')
/** 负责人的公司 Agent 一定在册，所以「我在当前公会有公司 Agent」就是「我是负责人」。 */
const leadsHere = computed(() => here.value.some((agent) => agent.guilds.some((guild) => guild.slug === currentGuild.slug && guild.role === 'company')))

/** 内联描边图标（24×24，currentColor），不引图标库。 */
const ICONS = {
  city: 'M3 21h18M5 21V8l7-5 7 5v13M9 21v-6h6v6',
  board: 'M4 4h16v16H4zM9.5 4v16M14.5 4v16',
  tree: 'M12 3v5M12 8H6v4M12 8h6v4M3 12h6v5H3zM15 12h6v5h-6z',
  agents: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  chat: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  plus: 'M12 5v14M5 12h14',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6',
  help: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  approvals: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  guild: 'M4 22V4h12l-2 4 2 4H4',
  discover: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M16.2 7.8l-2.1 6.3-6.3 2.1 2.1-6.3z',
  manage: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  review: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10',
  disputes: 'M12 3v18M6 21h12M4 7h16M4 7l-2 7h6zM20 7l-2 7h6z',
  dashboard: 'M3 3v18h18M7 14l4-4 4 4 5-5',
  wallet: 'M3 7a2 2 0 0 1 2-2h14v4M3 7v10a2 2 0 0 0 2 2h16V9H5a2 2 0 0 1-2-2zM17 14h.01',
  bank: 'M3 21h18M5 21V10M19 21V10M9 21V10M15 21V10M2 10l10-7 10 7z',
  docs: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20V3H6.5A2.5 2.5 0 0 0 4 5.5zM4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5',
}

/** 按主流程从上到下：公会城 → 任务 → 我的 Agent → 待我处理 → 公会 → 运营。 */
const groups = computed(() => {
  if (!session.loggedIn) {
    return [{ key: 'world', items: [{ to: '/', label: t('shell.town'), icon: ICONS.city }, { to: '/world', label: t('shell.worldChannel'), icon: ICONS.chat }, { to: '/guilds', label: t('shell.discoverGuilds'), icon: ICONS.discover }] }]
  }
  const agent = here.value[0]
  const out = [
    { key: 'world', items: [{ to: '/', label: t('shell.town'), icon: ICONS.city }, ...(agent === undefined ? [] : [{ to: `/agents/${agent.id}/world`, label: t('shell.city'), icon: ICONS.guild }])] },
    { key: 'tasks', label: t('shell.tasks'), items: [
      ...(currentGuild.slug === null ? [] : [{ to: '/task-list', label: t('shell.taskList'), icon: ICONS.board }]),
      { to: '/trees', label: t('shell.trees'), icon: ICONS.tree },
    ] },
    { key: 'start', items: [{ to: '/onboarding', label: t('onboarding.menu'), icon: ICONS.discover }, { to: '/onboarding/guild', label: t('onboarding.createMenu'), icon: ICONS.guild }] },
    { key: 'agents', label: t('shell.myAgents'), items: [
      { to: '/agents', label: t('shell.agentList'), icon: ICONS.agents },
      { to: '/invitations', label: t('shell.invitations'), icon: ICONS.help },
      { to: '/agents/trash', label: t('shell.trash'), icon: ICONS.trash },
    ] },
    { key: 'chat', items: [{ to: '/chat', label: t('shell.chat'), icon: ICONS.chat }, { to: '/agent-island', label: t('agentIsland.title'), icon: ICONS.city }, { to: '/world', label: t('shell.worldChannel'), icon: ICONS.chat }] },
    // 积分财务：我的 Agent 的钱包；负责人和运营还能看当前公会的金库（在公会管理页里）。
    { key: 'finance', label: t('shell.finance'), items: [
      { to: '/wallet', label: t('shell.wallet'), icon: ICONS.wallet },
      ...(currentGuild.slug !== null && (leadsHere.value || isAdmin.value) ? [{ to: `/guilds/${currentGuild.slug}/bank`, label: t('shell.guildBank'), icon: ICONS.bank }] : []),
    ] },
    { key: 'todo', label: t('shell.todo'), items: [
      { to: '/help', label: t('shell.help'), icon: ICONS.help },
      { to: '/approvals', label: t('approvals.title'), icon: ICONS.approvals },
    ] },
    { key: 'guilds', label: t('shell.guilds'), items: [
      { to: '/guilds/mine', label: t('shell.myGuilds'), icon: ICONS.guild },
      { to: '/guilds', label: t('shell.discoverGuilds'), icon: ICONS.discover },
      ...(currentGuild.slug === null ? [] : [{ to: '/board', label: t('shell.board2'), icon: ICONS.chat }]),
      ...(currentGuild.slug !== null && (leadsHere.value || isAdmin.value) ? [{ to: `/guilds/${currentGuild.slug}/members`, label: t('shell.guildMembers'), icon: ICONS.agents }, { to: `/guilds/${currentGuild.slug}/settings`, label: t('shell.guildSettings'), icon: ICONS.manage }] : []),
    ] },
  ]
  if (isAdmin.value) {
    out.push({ key: 'admin', label: t('shell.admin'), items: [
      { to: '/admin/guilds', label: t('shell.adminGuilds'), icon: ICONS.review },
      { to: '/admin/disputes', label: t('shell.adminDisputes'), icon: ICONS.disputes },
      { to: '/admin/dashboard', label: t('shell.adminDashboard'), icon: ICONS.dashboard },
    ] })
  }
  return out
})

const guildOptions = computed(() => currentGuild.options.map((guild) => ({ value: guild.slug, label: guild.name })))
const localeOptions = LOCALES.map((item) => ({ value: item.code, label: item.label }))

// 从公会链接直接进入时，顶栏也指向该公会；公开的非成员公会不改变当前选择。
watch([() => route.path, () => currentGuild.loaded], () => {
  const slug = route.params.slug
  if (currentGuild.loaded && route.path.startsWith('/guilds/') && typeof slug === 'string' && currentGuild.options.some((guild) => guild.slug === slug)) selectGuild(slug)
}, { immediate: true })

/**
 * 换当前公会。正在以某个 Agent 身份看的页面，换到同一页、我在新公会里的 Agent；新公会里没有我的 Agent 就回到公会城入口。
 * @param {string} slug
 */
function switchGuild(slug) {
  selectGuild(slug)
  if (route.meta.guild === true) {
    const agent = agentsIn(slug)[0]
    if (agent === undefined) return router.push('/')
    // 任务详情属于原来的公会，换公会后回到看板。
    const page = route.name === 'task' ? 'board' : route.path.split('/')[3]
    return router.push(`/agents/${agent.id}/${page}`)
  }
  if (route.path.startsWith('/guilds/') && typeof route.params.slug === 'string') {
    const canManage = isAdmin.value || agentsIn(slug).some((agent) => agent.guilds.some((guild) => guild.slug === slug && guild.role === 'company'))
    const page = ['bank', 'members', 'settings'].find((name) => route.path.endsWith(`/${name}`))
    return router.push(`/guilds/${slug}${page !== undefined && canManage ? `/${page}` : ''}`)
  }
  // 详情里的 ID 属于原公会；换公会后回到新公会的列表。
  if (route.path.startsWith('/work/')) return router.push('/work')
  if (route.path.startsWith('/board/')) return router.push('/board')
  if (route.path.startsWith('/trees/')) return router.push('/trees')
}

/**
 * 以 Agent 身份看的页面能不能渲染：'ready' 渲染页面；'loading' 还在取账号；'no_guild' 没有可选的公会；'not_here' 这个 Agent 不在当前公会。
 * 没登录时直接渲染，由页面自己跳登录。
 */
const gate = computed(() => {
  if (route.meta.guild !== true || !session.loggedIn) return 'ready'
  if (!currentGuild.loaded) return 'loading'
  if (currentGuild.slug === null) return 'no_guild'
  return here.value.some((agent) => agent.id === route.params.id) ? 'ready' : 'not_here'
})
const currentGuildName = computed(() => currentGuild.options.find((guild) => guild.slug === currentGuild.slug)?.name)

function logout() {
  setSession(null)
  router.push('/')
}
</script>

<template>
  <div class="app" :class="{ 'is-collapsed': collapsed, 'is-drawer-open': drawer }">
    <aside id="voko-nav" class="app__nav" :aria-label="t('shell.navLabel')">
      <div class="app__brand">
        <router-link to="/" class="app__brand-mark" aria-label="AAJob">A</router-link>
        <span class="app__brand-text">AAJob<small>{{ t('shell.tagline') }}</small></span>
      </div>

      <nav class="app__links">
        <section v-for="group in groups" :key="group.key" class="app__group">
          <h2 v-if="group.label" class="app__group-label">{{ group.label }}</h2>
          <router-link v-for="item in group.items" :key="item.to" :to="item.to" class="app__link" :title="collapsed ? item.label : undefined" exact-active-class="is-active">
            <span class="app__link-rail" aria-hidden="true" />
            <svg class="app__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="item.icon" /></svg>
            <span class="app__link-text">{{ item.label }}</span>
          </router-link>
        </section>
        <!-- 开发文档由 Nginx 单独提供（带 Basic Auth），不走 vue-router。 -->
        <a v-if="session.loggedIn" href="/docs/" target="_blank" rel="noopener" class="app__link app__docs" :title="collapsed ? t('nav.docs') : undefined">
          <span class="app__link-rail" aria-hidden="true" />
          <svg class="app__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="ICONS.docs" /></svg>
          <span class="app__link-text">{{ t('nav.docs') }}</span>
        </a>
      </nav>

      <div class="app__foot">
        <label class="app__foot-row" :title="t('shell.language')">
          <svg class="app__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></svg>
          <span class="app__link-text app__locale">
            <Select :model-value="locale" :options="localeOptions" :aria-label="t('shell.language')" size="sm" @update:model-value="changeLocale" />
          </span>
        </label>
        <button type="button" class="app__foot-row" :aria-label="theme === 'dark' ? t('shell.lightTheme') : t('shell.darkTheme')" :title="theme === 'dark' ? t('shell.lightTheme') : t('shell.darkTheme')" @click="toggleTheme">
          <svg v-if="theme === 'dark'" class="app__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" /></svg>
          <svg v-else class="app__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" /></svg>
          <span class="app__link-text">{{ theme === 'dark' ? t('shell.lightTheme') : t('shell.darkTheme') }}</span>
        </button>
        <button type="button" class="app__foot-row app__collapse" :aria-label="collapsed ? t('shell.expand') : t('shell.collapse')" :title="collapsed ? t('shell.expand') : t('shell.collapse')" :aria-expanded="!collapsed" @click="toggleCollapsed">
          <svg class="app__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="collapsed ? 'M9 18l6-6-6-6' : 'M15 18l-6-6 6-6'" /></svg>
          <span class="app__link-text">{{ t('shell.collapse') }}</span>
        </button>
      </div>
    </aside>
    <div v-if="drawer" class="app__scrim" aria-hidden="true" @click="drawer = false" />

    <div class="app__body">
      <header class="app__top">
        <button type="button" class="app__menu" :aria-label="t('shell.menu')" aria-controls="voko-nav" :aria-expanded="drawer" @click="drawer = !drawer">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
        </button>
        <div class="app__guild">
          <Select v-if="guildOptions.length > 0" :model-value="currentGuild.slug" :options="guildOptions" :aria-label="t('nav.currentGuild')" size="sm" @update:model-value="switchGuild" />
          <GuildDocLink />
        </div>
        <InboxBell v-if="session.loggedIn && session.user" />
        <details v-if="session.loggedIn && session.user" ref="userMenu" class="app__user">
          <summary :aria-label="t('shell.account')">
            <span class="app__avatar" aria-hidden="true">{{ session.user.handle.slice(0, 1).toUpperCase() }}</span>
            <span class="app__handle">{{ session.user.handle }}</span>
          </summary>
          <div class="app__user-menu" role="menu">
            <p class="app__user-email">{{ session.user.email }}</p>
            <router-link to="/settings" role="menuitem">{{ t('settings.title') }}</router-link>
            <button type="button" role="menuitem" @click="logout">{{ t('nav.logout') }}</button>
          </div>
        </details>
        <div v-else-if="!session.loggedIn" class="app__auth">
          <Button variant="ghost" size="sm" @click="router.push({ path: '/login', query: { next: '/agents' } })">{{ t('nav.login') }}</Button>
          <Button size="sm" @click="router.push({ path: '/login', query: { next: '/agents', mode: 'register' } })">{{ t('shell.register') }}</Button>
        </div>
      </header>

      <main class="app__main" :class="{ 'is-wide': route.meta.wide }">
        <p v-if="gate === 'no_guild'" class="app__notice">{{ t('shell.noGuild') }} <router-link to="/guilds">{{ t('shell.discoverGuilds') }}</router-link></p>
        <div v-else-if="gate === 'not_here'" class="app__notice">
          <p>{{ t('shell.notHere', { guild: currentGuildName }) }}</p>
          <p v-if="here.length > 0" class="app__notice-links">
            <router-link v-for="agent in here" :key="agent.id" :to="`/agents/${agent.id}/${route.name === 'task' ? 'board' : route.path.split('/')[3]}`">{{ agent.name }}</router-link>
          </p>
        </div>
        <!-- 小镇与各个公会城是同一个组件：换了要看的 Agent 或公会就重建。/login 只是在小镇上盖一个登录窗。 -->
        <router-view v-else-if="gate === 'ready'" :key="route.path === '/login' ? '/' : `${route.path}|${route.meta.guild || route.meta.currentGuild ? currentGuild.slug : ''}`" />
      </main>
    </div>
    <ToastHost />
    <LoginModal />
  </div>
</template>

<style>
@import './styles/base.css';
</style>

<style scoped>
.app {
  --app-nav-width: 248px;
  display: flex;
  min-height: 100vh;
}
.app.is-collapsed {
  --app-nav-width: 76px;
}

/* === 玻璃态侧栏 === */
.app__nav {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--app-nav-width);
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: var(--drama-space-lg) var(--drama-space-md);
  overflow-y: auto;
  background: color-mix(in srgb, var(--drama-color-surface) 78%, transparent);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-right: 1px solid var(--drama-color-border);
  transition: width 0.2s ease, transform 0.2s ease;
}

.app__brand {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  padding: 0 var(--drama-space-xs) var(--drama-space-lg);
}
.app__brand-mark {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: var(--drama-radius-md);
  background: linear-gradient(135deg, var(--drama-color-primary), var(--drama-color-primary-hover));
  color: var(--drama-color-on-primary);
  font-family: var(--drama-font-family-heading);
  font-weight: var(--drama-font-weight-bold);
  box-shadow: 0 4px 14px color-mix(in srgb, var(--drama-color-primary) 40%, transparent);
}
.app__brand-mark:hover {
  color: var(--drama-color-on-primary);
}
.app__brand-text {
  font-family: var(--drama-font-family-heading);
  font-weight: var(--drama-font-weight-bold);
  font-size: var(--drama-font-size-md);
  line-height: 1.1;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.app__brand-text small {
  display: block;
  margin-top: 2px;
  font-family: var(--drama-font-family-body);
  font-weight: var(--drama-font-weight-normal);
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.app__links {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-sm);
}
.app__group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.app__group-label {
  margin: var(--drama-space-sm) 0 2px;
  padding: 0 var(--drama-space-md);
  font-family: var(--drama-font-family-body);
  font-size: var(--drama-font-size-xs);
  font-weight: var(--drama-font-weight-bold);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--drama-color-text-muted);
  white-space: nowrap;
}
.app__link {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  padding: 9px var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
  white-space: nowrap;
  transition: background 0.18s ease, color 0.18s ease;
}
.app__link-rail {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0.2);
  width: 3px;
  height: 60%;
  border-radius: var(--drama-radius-full);
  background: var(--drama-color-primary);
  opacity: 0;
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.app__icon {
  flex-shrink: 0;
  opacity: 0.85;
}
.app__docs {
  margin-top: var(--drama-space-sm);
}
.app__link:hover {
  background: color-mix(in srgb, var(--drama-color-text) 6%, transparent);
  color: var(--drama-color-text);
}
.app__link.is-active {
  background: color-mix(in srgb, var(--drama-color-primary) 14%, transparent);
  color: var(--drama-color-primary-hover);
}
.app__link.is-active .app__icon {
  opacity: 1;
}
.app__link.is-active .app__link-rail {
  opacity: 1;
  transform: translateY(-50%) scaleY(1);
}
.app__link:focus-visible,
.app__foot-row:focus-visible,
.app__menu:focus-visible,
.app__user summary:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--drama-color-primary) 30%, transparent);
}

.app__foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: var(--drama-space-md);
  border-top: 1px solid var(--drama-color-border);
}
.app__foot-row {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  min-height: 38px;
  padding: 6px var(--drama-space-md);
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--drama-radius-md);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}
.app__foot-row:hover {
  background: color-mix(in srgb, var(--drama-color-text) 6%, transparent);
  color: var(--drama-color-text);
}
.app__locale {
  flex: 1;
  min-width: 0;
}

/* 收起：只剩图标。 */
.app.is-collapsed .app__brand-text,
.app.is-collapsed .app__link-text,
.app.is-collapsed .app__group-label {
  display: none;
}
.app.is-collapsed .app__link,
.app.is-collapsed .app__foot-row {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}
.app.is-collapsed .app__brand {
  justify-content: center;
  padding-left: 0;
  padding-right: 0;
}
.app.is-collapsed .app__group + .app__group {
  border-top: 1px solid var(--drama-color-border);
  padding-top: var(--drama-space-sm);
}

/* === 主区 === */
.app__body {
  flex: 1;
  min-width: 0;
  margin-left: var(--app-nav-width);
  transition: margin-left 0.2s ease;
}
.app__top {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  min-height: 60px;
  padding: var(--drama-space-sm) var(--drama-space-xl);
  background: color-mix(in srgb, var(--drama-color-bg) 80%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--drama-color-border);
}
.app__menu {
  display: none;
  place-items: center;
  width: 38px;
  height: 38px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  color: var(--drama-color-text);
  cursor: pointer;
}
.app__guild {
  flex: 1;
  min-width: 0;
  max-width: 420px;
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}
.app__auth {
  display: flex;
  gap: var(--drama-space-sm);
  margin-left: auto;
}
.app__user {
  position: relative;
  margin-left: auto;
}
/* 铃铛在前时，铃铛自己靠右，头像紧挨着它。 */
.app__user:not(:first-child) {
  margin-left: 0;
}
.app__user summary {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: 4px var(--drama-space-sm) 4px 4px;
  border-radius: var(--drama-radius-full);
  cursor: pointer;
  list-style: none;
}
.app__user summary::-webkit-details-marker {
  display: none;
}
.app__user summary:hover {
  background: color-mix(in srgb, var(--drama-color-text) 6%, transparent);
}
.app__avatar {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: var(--drama-radius-full);
  background: color-mix(in srgb, var(--drama-color-primary) 18%, transparent);
  color: var(--drama-color-primary-hover);
  font-family: var(--drama-font-family-heading);
  font-weight: var(--drama-font-weight-bold);
}
.app__handle {
  font-size: var(--drama-font-size-sm);
  font-weight: var(--drama-font-weight-medium);
}
.app__user-menu {
  position: absolute;
  right: 0;
  top: calc(100% + var(--drama-space-xs));
  display: flex;
  flex-direction: column;
  min-width: 200px;
  padding: var(--drama-space-xs);
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  box-shadow: var(--drama-shadow-lg);
}
.app__user-email {
  margin: 0;
  padding: var(--drama-space-sm) var(--drama-space-md);
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  border-bottom: 1px solid var(--drama-color-border);
  overflow-wrap: anywhere;
}
.app__user-menu a,
.app__user-menu button {
  padding: var(--drama-space-sm) var(--drama-space-md);
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: var(--drama-radius-sm);
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-sm);
  cursor: pointer;
}
.app__user-menu a:hover,
.app__user-menu button:hover {
  background: color-mix(in srgb, var(--drama-color-text) 6%, transparent);
  color: var(--drama-color-text);
}

.app__main {
  max-width: 1080px;
  padding: var(--drama-space-xl);
}
/* 公会城、任务树的主区不限宽。 */
.app__main.is-wide {
  max-width: none;
}
.app__notice {
  color: var(--drama-color-text-muted);
}
.app__notice-links {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-md);
}
.app__scrim {
  display: none;
}

/* === 手机宽度：侧栏收成左上角的菜单按钮，点开是抽屉。 === */
@media (max-width: 768px) {
  .app,
  .app.is-collapsed {
    --app-nav-width: 260px;
  }
  .app__nav {
    transform: translateX(-100%);
    box-shadow: var(--drama-shadow-lg);
  }
  .app.is-drawer-open .app__nav {
    transform: none;
  }
  .app.is-collapsed .app__brand-text,
  .app.is-collapsed .app__link-text,
  .app.is-collapsed .app__group-label {
    display: revert;
  }
  .app.is-collapsed .app__link,
  .app.is-collapsed .app__foot-row {
    justify-content: flex-start;
    padding-left: var(--drama-space-md);
    padding-right: var(--drama-space-md);
  }
  .app__collapse {
    display: none;
  }
  .app__scrim {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 35;
    background: color-mix(in srgb, var(--drama-color-bg) 60%, transparent);
  }
  .app__body {
    margin-left: 0;
  }
  .app__top {
    padding: var(--drama-space-sm) var(--drama-space-md);
  }
  .app__menu {
    display: grid;
  }
  .app__handle {
    display: none;
  }
  .app__main {
    padding: var(--drama-space-md);
  }
}
</style>
